import TiptapLink from '@tiptap/extension-link';
import type { EditorProps } from '@tiptap/pm/view';

/**
 * Configured TipTap Link extension shared across all three email composers
 * (EmailComposer, EmailTemplateEditor, SequenceEmailComposer).
 *
 *  - target='_blank' + rel='noopener noreferrer nofollow' on saved <a> tags
 *    (deliverability + safety — same rels we apply when sending real email)
 *  - autolink + linkOnPaste so typed/pasted URLs become links automatically
 *  - renderHTML override adds a `title` attribute mirroring the href, so
 *    hover gives a native browser tooltip showing where the link goes
 *  - openOnClick=false: editor-mode clicks don't navigate. Combined with
 *    linkEditorProps() below, only Ctrl/Cmd+click navigates — single click
 *    keeps the caret inside the link for editing.
 */
export function createLinkExtension() {
  return TiptapLink.extend({
    renderHTML(props) {
      // Delegate to upstream renderHTML so its isAllowedUri / protocols
      // gating still runs (drops disallowed schemes like javascript: and
      // data:). Then patch the resulting attrs to add a `title` attribute
      // that mirrors href, giving us a native browser hover tooltip.
      //
      // Upstream returns ['a', attrs, 0]. If anything looks off (shape
      // changed in a future TipTap version), fall back to a safe minimal
      // <a> with href stripped — losing the title is preferable to losing
      // the URI safety check.
      const rendered = this.parent?.(props);
      if (
        !Array.isArray(rendered)
        || rendered.length < 2
        || typeof rendered[1] !== 'object'
        || rendered[1] === null
      ) {
        return rendered ?? ['a', {}, 0];
      }
      const attrs = rendered[1] as Record<string, unknown>;
      const href = typeof attrs.href === 'string' ? attrs.href : '';
      if (href) {
        attrs.title = `${href}\nCtrl/⌘+click to open in a new tab`;
      }
      return rendered;
    },
  }).configure({
    openOnClick: false,
    autolink: true,
    linkOnPaste: true,
    HTMLAttributes: {
      class: 'text-primary underline underline-offset-4',
      target: '_blank',
      rel: 'noopener noreferrer nofollow',
    },
  });
}

/**
 * editorProps fragment that opens a link in a new tab when the user
 * Ctrl/Cmd+clicks anywhere inside it. Returns true to consume the event
 * so the editor doesn't also move the caret. Compose into the editor's
 * `editorProps` with spread:
 *
 *     editorProps: {
 *       attributes: { ... },
 *       ...linkEditorProps(),
 *       handlePaste: ...,
 *       handleDrop: ...,
 *     }
 *
 * If the editor already has a `handleClick`, merge logic manually.
 */
export function linkEditorProps(): Partial<EditorProps> {
  return {
    handleClick(view, pos, event) {
      if (!(event.ctrlKey || event.metaKey)) return false;
      const $pos = view.state.doc.resolve(pos);
      const linkMark = $pos.marks().find((m) => m.type.name === 'link');
      const href = linkMark?.attrs?.href;
      if (typeof href === 'string' && href) {
        window.open(href, '_blank', 'noopener,noreferrer');
        event.preventDefault();
        return true;
      }
      return false;
    },
  };
}
